import type { SupabaseClient } from '@supabase/supabase-js';
import type { BoardDayUpsert, BoardWriteStore } from '../ports.js';
import type { ActivityPresence, BoardAbsence } from '../activityBoard.js';

/**
 * `BoardWriteStore` over the 0055 RPCs, with the BROWSER's authed client.
 *
 * The two `set*` methods call an RPC rather than issuing the upsert and the
 * companion delete themselves. That is the entire point of migration 0055:
 * doing it here in two `await`s is what the five previous implementations did,
 * and what left the board able to show an employee as both present and absent.
 *
 * The RPCs are SECURITY INVOKER, so RLS still decides what the caller may
 * write — this adapter grants nothing. A refusal surfaces as a thrown error
 * with the database's own message, including 0055's explicit
 * « could not be cleared » check violations, which are the cases that used to
 * corrupt silently.
 */
export class SupabaseBoardWriteStore implements BoardWriteStore {
  constructor(private readonly db: SupabaseClient) {}

  private async call(fn: string, args: Record<string, unknown>): Promise<void> {
    const { error } = await this.db.rpc(fn, args);
    if (error) throw new Error(`${fn}: ${error.message}`);
  }

  setPresence(input: {
    activityId: number;
    employeeId: string;
    date: string;
    status: 'declared' | 'assigned';
    source: ActivityPresence['source'];
    declaredBy: string | null;
  }): Promise<void> {
    return this.call('board_set_presence', {
      p_activity_id: input.activityId,
      p_employee_id: input.employeeId,
      p_date: input.date,
      p_status: input.status,
      p_source: input.source,
      p_declared_by: input.declaredBy,
    });
  }

  async clearPresence(activityId: number, employeeId: string, date: string): Promise<void> {
    // A single-table delete needs no transaction of its own — no RPC.
    const { error } = await this.db
      .from('board_activity_presence')
      .delete()
      .eq('activity_id', activityId)
      .eq('employee_id', employeeId)
      .eq('date', date);
    if (error) throw new Error(`board_activity_presence.delete: ${error.message}`);
  }

  setAbsence(input: {
    employeeId: string;
    date: string;
    source: BoardAbsence['source'];
    declaredBy: string | null;
  }): Promise<void> {
    return this.call('board_set_absence', {
      p_employee_id: input.employeeId,
      p_date: input.date,
      p_source: input.source,
      p_declared_by: input.declaredBy,
    });
  }

  async clearAbsence(employeeId: string, date: string): Promise<void> {
    const { error } = await this.db
      .from('board_absence')
      .delete()
      .eq('employee_id', employeeId)
      .eq('date', date);
    if (error) throw new Error(`board_absence.delete: ${error.message}`);
  }

  /**
   * THE snake_case mapping for the demand grid. It used to live in
   * `apps/reception/src/team/logic.ts`, which meant the desk's form module knew
   * PostgREST's column names. One multi-row upsert, so a week saved from the
   * Semaine tab is still all-or-nothing.
   */
  async saveDays(days: BoardDayUpsert[]): Promise<void> {
    if (days.length === 0) return;
    const { error } = await this.db.from('board_days').upsert(
      days.map((d) => ({
        date: d.date,
        departures: d.departures,
        arrivals: d.arrivals,
        occupancy: d.occupancy,
        stayovers: d.stayovers,
        breakfasts: d.breakfasts,
        half_boards: d.halfBoards,
        diners: d.diners,
        covers: d.covers,
        note: d.note,
      })),
      { onConflict: 'date' },
    );
    if (error) throw new Error(`board_days.upsert: ${error.message}`);
  }

  async saveOccupancyChain(rows: { date: string; occupancy: number; stayovers: number }[]): Promise<void> {
    if (rows.length === 0) return;
    // Partial payload on purpose — PostgREST updates only the columns present,
    // leaving the desk's arrivals/départs/couverts for those days untouched.
    // Picked explicitly (not `upsert(rows, …)`): callers pass propagateOccupancy's
    // ChainResult, which also carries `inconsistent` — TS's excess-property
    // check only fires on object literals, not on a variable, so that extra
    // field sailed through typecheck and 400'd at Supabase ("could not find
    // the 'inconsistent' column"). One caller can forget to strip it; this
    // can't.
    const payload = rows.map((r) => ({ date: r.date, occupancy: r.occupancy, stayovers: r.stayovers }));
    const { error } = await this.db.from('board_days').upsert(payload, { onConflict: 'date' });
    if (error) throw new Error(`board_days.occupancyChain: ${error.message}`);
  }

  async saveDeskDay(row: { date: string; diners: number; note: string | null }): Promise<void> {
    // An RPC, not an upsert: `covers` must be recombined with the row's OWN
    // half_boards, which the feed writes and this client cannot know is
    // current. PostgREST cannot express that; migration 0131 can.
    const { error } = await this.db.rpc('board_save_desk_day', {
      p_date: row.date,
      p_diners: row.diners,
      p_note: row.note,
    });
    if (error) throw new Error(`board_days.deskDay: ${error.message}`);
  }

  async saveOverride(input: {
    activityId: number;
    date: string;
    windowStart: string | null;
    windowEnd: string | null;
    required: boolean;
    note: string | null;
  }): Promise<void> {
    const { error } = await this.db.from('board_activity_overrides').upsert(
      {
        activity_id: input.activityId,
        date: input.date,
        window_start: input.windowStart,
        window_end: input.windowEnd,
        required: input.required,
        note: input.note,
      },
      { onConflict: 'activity_id,date' },
    );
    if (error) throw new Error(`board_activity_overrides.upsert: ${error.message}`);
  }

  async clearOverride(overrideId: number): Promise<void> {
    const { error } = await this.db.from('board_activity_overrides').delete().eq('id', overrideId);
    if (error) throw new Error(`board_activity_overrides.delete: ${error.message}`);
  }
}
